/** Only UI keys allowed in localStorage — never business data. */
export const FINVOX_LOCAL_STORAGE_ALLOWLIST = [
  'finvox_locale',
  'finvox_sidebar_collapsed',
  'finvox_last_org_id'
] as const;

export type FinvoxLocalStorageKey = (typeof FINVOX_LOCAL_STORAGE_ALLOWLIST)[number];

const LAST_ORG_PREFIX = 'finvox_last_org_id:';

export function lastOrgIdStorageKey(uid: string): string {
  return `${LAST_ORG_PREFIX}${uid}`;
}

function isAllowedLocalStorageKey(key: string): key is FinvoxLocalStorageKey {
  if ((FINVOX_LOCAL_STORAGE_ALLOWLIST as readonly string[]).includes(key)) return true;
  return key.startsWith(LAST_ORG_PREFIX) && key.length > LAST_ORG_PREFIX.length;
}

export function safeLocalStorageSet(key: string, value: string): void {
  if (!isAllowedLocalStorageKey(key)) {
    throw new Error(
      `[FinVox] localStorage key "${key}" is not allowed. Business data must use Firestore only.`
    );
  }
  localStorage.setItem(key, value);
}

export function safeLocalStorageGet(key: string): string | null {
  if (!isAllowedLocalStorageKey(key)) return null;
  return localStorage.getItem(key);
}

export function clearUserLastOrgId(uid: string): void {
  try {
    localStorage.removeItem(lastOrgIdStorageKey(uid));
  } catch {
    /* ignore */
  }
}

/** Remove legacy global org hint and per-user org hints on sign-out. */
export function clearAllLastOrgIdHints(): void {
  try {
    localStorage.removeItem('finvox_last_org_id');
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(LAST_ORG_PREFIX)) toRemove.push(key);
    }
    for (const key of toRemove) localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
